import { useEffect } from 'react';
import { Field, Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { Box, Button, Stack } from '@mui/material';
import { TextField as FormikTextField } from 'formik-mui';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { EmployeePickerField } from 'features/user/user-picker/employee-picker';
import { ManagerPickerField } from 'features/user/user-picker/manager-picker';
import { TaskStatusPickerField, TaskStatusEnum } from 'shared/ui/formik-fields/task-status-picker-field';
import { VALIDATION_REQUIRED_FIELD } from 'utils/form-tools';

export type TaskFormValues = {
  theme: string;
  description?: string | null;
  managerId: number | null;
  employeeIds: number[];
  dueDate?: Date | null;
  status: TaskStatusEnum;
};

const defaultValues: TaskFormValues = {
  theme: '',
  description: '',
  managerId: null,
  employeeIds: [],
  dueDate: null,
  status: TaskStatusEnum.NEW
};

export const TaskForm = ({
  initialValues = defaultValues,
  onSubmit,
  onCancel
}: {
  initialValues?: TaskFormValues;
  onSubmit: (values: TaskFormValues, helpers: FormikHelpers<TaskFormValues>) => void;
  onCancel: () => void;
}) => {
  const validate = (values: TaskFormValues): FormikErrors<TaskFormValues> => {
    const errors: FormikErrors<TaskFormValues> = {};
    if (!values.theme || values.theme.trim() === '') errors.theme = VALIDATION_REQUIRED_FIELD;
    if (values.employeeIds.length === 0) {
      errors.employeeIds = VALIDATION_REQUIRED_FIELD;
    }
    if (values.status === undefined || values.status === null) errors.status = VALIDATION_REQUIRED_FIELD;
    return errors;
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} enableReinitialize>
      {(formikProps) => <TaskFormFields formik={formikProps} onCancel={onCancel} />}
    </Formik>
  );
};

const REQUIRED_FIELDS: (keyof TaskFormValues)[] = ['theme', 'employeeIds', 'status'];

const TaskFormFields = ({ formik, onCancel }: { formik: FormikProps<TaskFormValues>; onCancel: () => void }) => {
  useEffect(() => {
    if (formik.submitCount > 0) {
      REQUIRED_FIELDS.forEach((field) => {
        if (!formik.touched[field]) {
          formik.setFieldTouched(field, true, false);
        }
      });
    }
  }, [formik.submitCount, formik.setFieldTouched, formik.touched]);

  const { values } = formik;

  return (
    <Form>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Field fullWidth name="theme" label="Тема" component={FormikTextField} required />
        <Field fullWidth name="description" label="Описание" component={FormikTextField} multiline minRows={2} />
        <Stack direction="row" spacing={2}>
          <Field name="managerId" label="Руководитель" component={ManagerPickerField} required clearable />
          <Field
            name="employeeIds"
            label="Сотрудники"
            component={EmployeePickerField}
            managerId={values.managerId ?? null}
            multiple
            clearable
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Field name="dueDate" label="Срок" component={DatePickerField} />
          <Field name="status" component={TaskStatusPickerField} label="Статус" required />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={onCancel} color="inherit">
            Отмена
          </Button>
          <Button variant="contained" type="submit">
            Сохранить
          </Button>
        </Box>
      </Stack>
    </Form>
  );
};
